import earcut from 'earcut';
import { FRAME_ASPECT, WORLD_H } from './constants';

export type Pt = [number, number];
export type Rect = { x: number; y: number; w: number; h: number };

export type Level = {
  id: number;
  /** 정규화 좌표(높이 1, y 아래로 증가). 단순 다각형. */
  obstacles: { pts: Pt[]; /** 구멍(안쪽 빈 공간). 공이 그 안에 있는 판이 있다 */ holes?: Pt[][] }[];
  balls: { blue: Pt; pink: Pt; r: number };
  /** 그리기 금지 영역(원작의 그물 무늬). 물리에는 없고 그리기만 막는다. */
  zones?: { kind: 'nodraw'; pts: Pt[] }[];
  /** 탄성 면(원작의 초록 지그재그). 정적 선분, 반발 1. */
  bouncy?: { a: Pt; b: Pt }[];
  /** 움직이는 물체(원작의 연한 회색 컵·원·호). 동적 강체. */
  dynamics?: (({ pts: Pt[] } | { circle: [number, number, number] }) & { /** 풍차: 이 점(정규화)에 축이 박혀 제자리에서 돈다 */ pin?: Pt })[];
  /** 공은 통과하고 선만 걸리는 바(원작의 ○ 표시 연한 바). 정규화 사각형. */
  ghosts?: Rect[];
  /** 컨베이어(원작의 기어 달린 바). 중심·크기·각도(도, 화면 x축 기준 시계방향). dir 1 = 벨트의 +x 방향으로 실어 나름. */
  conveyors?: { cx: number; cy: number; w: number; h: number; angle?: number; dir: 1 | -1; speed?: number }[];
  /** 얼음(원작의 하늘색 상자). 그린 선이 닿으면 사라진다. */
  ice?: Rect[];
  src?: { t: number; flags: string[]; color?: number[] | null };
};

export type LevelFile = { version: 1; frame: { w: number; h: number }; levels: Level[] };

/** 정규화 → 월드(m). y 는 위로 증가. */
export function toWorld(p: Pt): Pt {
  return [p[0] * WORLD_H, (1 - p[1]) * WORLD_H];
}
export function toNorm(p: Pt): Pt {
  return [p[0] / WORLD_H, 1 - p[1] / WORLD_H];
}

export function validateLevel(l: Level): string[] {
  const errs: string[] = [];
  if (!Number.isInteger(l.id) || l.id < 1) errs.push('id');
  if (!Array.isArray(l.obstacles)) errs.push('obstacles');
  else l.obstacles.forEach((o, i) => { if (!o.pts || o.pts.length < 3) errs.push(`obstacle[${i}]: <3 pts`); });
  const inside = (p: Pt) => p[0] >= 0 && p[0] <= FRAME_ASPECT && p[1] >= 0 && p[1] <= 1;
  if (!l.balls || !inside(l.balls.blue)) errs.push('balls.blue');
  if (!l.balls || !inside(l.balls.pink)) errs.push('balls.pink');
  if (!l.balls || !(l.balls.r > 0.02 && l.balls.r < 0.05)) errs.push('balls.r');
  return errs;
}

function area2(a: Pt, b: Pt, c: Pt): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}
/** 다각형(구멍 포함 가능) → 삼각형. earcut(MIT). */
export function triangulate(poly: Pt[], holes: Pt[][] = []): [Pt, Pt, Pt][] {
  const flat: number[] = [];
  const holeIdx: number[] = [];
  const all: Pt[] = [];
  for (const p of poly) { flat.push(p[0], p[1]); all.push(p); }
  for (const h of holes) {
    if (h.length < 3) continue;
    holeIdx.push(all.length);
    for (const p of h) { flat.push(p[0], p[1]); all.push(p); }
  }
  const idx = earcut(flat, holeIdx.length ? holeIdx : undefined, 2);
  const out: [Pt, Pt, Pt][] = [];
  for (let i = 0; i + 2 < idx.length; i += 3) {
    const a = all[idx[i]!]!, b = all[idx[i + 1]!]!, c = all[idx[i + 2]!]!;
    if (Math.abs(area2(a, b, c)) < 1e-12) continue;
    out.push([a, b, c]);
  }
  return out;
}

export function polygonArea(poly: Pt[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!, b = poly[(i + 1) % poly.length]!;
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s) / 2;
}
